// src/components/draft/DraftQueuePanel.js - Player Queue for Draft
import React from 'react';
import { X, List, ChevronUp, ChevronDown } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import PlayerLink from '../PlayerLink';

/**
 * DraftQueuePanel - Shows queued players for drafting
 */
const DraftQueuePanel = ({
  queue = [],
  onRemove,
  onMoveUp,
  onMoveDown,
  onSelectPlayer,
  onClear
}) => {
  return (
    <div className={`${dynastyTheme.components.card.base} p-3`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <List className={`w-4 h-4 ${dynastyTheme.classes.text.primary}`} />
          <h3 className={`text-base font-bold ${dynastyTheme.classes.text.white}`}>
            Queue
          </h3>
          <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
            ({queue.length})
          </span>
        </div>
        {queue.length > 0 && (
          <button
            onClick={onClear}
            className={`text-xs ${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.error}`}
          >
            Clear
          </button>
        )}
      </div>

      {queue.length === 0 ? (
        <div className={`text-center py-3 ${dynastyTheme.classes.text.neutralLight} text-xs`}>
          No players queued
        </div>
      ) : (
        <div className="space-y-1 max-h-48 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#ca8a04 transparent' }}>
          {queue.map((player, index) => {
            const playerName = player?.info?.full_name || 'Unknown';
            const position = player?.info?.position || '-';
            const price = player?.financial?.baseline_price || 0;
            const playerId = player?.ids?.league_player || player?.league_player_id;

            return (
              <div
                key={playerId || index}
                className={`flex items-center gap-2 p-1.5 rounded ${dynastyTheme.classes.bg.darkLighter} hover:bg-neutral-700/50`}
              >
                {/* Queue position */}
                <span className={`w-4 text-xs font-bold ${dynastyTheme.classes.text.primary}`}>
                  {index + 1}
                </span>

                {/* Player info - clickable to select */}
                <button
                  onClick={() => onSelectPlayer && onSelectPlayer(player)}
                  className={`flex-1 text-left text-xs ${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.text.primary}`}
                >
                  <PlayerLink mlbId={player?.ids?.mlb || null} name={playerName} style={{ fontWeight: 500, color: '#f9fafb' }} />
                  <span className={`ml-1 ${dynastyTheme.classes.text.warning}`}>{position}</span>
                  <span className={`ml-1 ${dynastyTheme.classes.text.neutralLight}`}>${Math.round(price)}</span>
                </button>

                {/* Move up/down buttons */}
                <div className="flex flex-col">
                  <button
                    onClick={() => onMoveUp && onMoveUp(index)}
                    disabled={index === 0}
                    className={`p-0.5 ${index === 0 ? 'opacity-30' : `hover:${dynastyTheme.classes.text.primary}`}`}
                  >
                    <ChevronUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => onMoveDown && onMoveDown(index)}
                    disabled={index === queue.length - 1}
                    className={`p-0.5 ${index === queue.length - 1 ? 'opacity-30' : `hover:${dynastyTheme.classes.text.primary}`}`}
                  >
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </div>

                {/* Remove button */}
                <button
                  onClick={() => onRemove && onRemove(index)}
                  className={`p-1 rounded hover:bg-red-500/20`}
                >
                  <X className={`w-3 h-3 ${dynastyTheme.classes.text.neutralLight} hover:text-red-400`} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DraftQueuePanel;
